import React, {useState, useEffect, useContext, useRef} from "react";
import {useHistory, useParams} from "react-router-dom";
import classNames from "classnames";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Button,
} from "reactstrap";

import {
    Tooltip,
    OverlayTrigger
} from "react-bootstrap";

import ReactTable from "components/ReactTable/ReactTable.js";
import NotificationAlert from "react-notification-alert";
import SweetAlert from "react-bootstrap-sweetalert";
import rest from "../../API/api";
import { UserLoginContext } from "../../context/UserLoginContext";
import { authenticatedFetch } from "../../services/authService";

const WaiverSubmissionsTable = () => {
    const { waiverId } = useParams();
    const [userState, setUserState] = useContext(UserLoginContext);
    const notificationAlertRef = useRef(null);
    const history = useHistory();
    const [dataLoaded, setDataLoaded] = useState(false);
    const [data, setData] = React.useState([]);
    const [submissions, setSubmissions] = React.useState([]);
    const [waiverInfo, setWaiverInfo] = React.useState(null);
    const [alert, setalert] = useState(null);

    const notify = (type, message) => {
        const options = {
            place: "tc",
            message: <div>{message}</div>,
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    const hideAlert = () => {
        setalert(null);
    };

    const deleteSubmission = async (submissionId) => {
        try {
            const response = await authenticatedFetch(`${rest.waiversubmissions}/${submissionId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete submission');
            }

            return true;
        } catch (error) {
            console.error('Error deleting submission:', error);
            throw error;
        }
    };

    const deleteSubmissionAlert = (submissionId, submissionName) => {
        setalert(
            <SweetAlert
                warning
                style={{ display: "block", marginTop: "-100px" }}
                title="Are you sure?"
                onConfirm={async () => {
                    try {
                        await deleteSubmission(submissionId);
                        hideAlert();
                        notify("success", "Waiver submission has been deleted successfully!");
                        // Refresh the submissions list
                        setDataLoaded(!dataLoaded);
                    } catch (error) {
                        hideAlert();
                        notify("danger", "Failed to delete waiver submission");
                    }
                }}
                onCancel={() => hideAlert()}
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="default"
                confirmBtnText="Yes, delete it!"
                cancelBtnText="Cancel"
                showCancel
            >
                You will not be able to recover the submission for {submissionName}!
            </SweetAlert>
        );
    };

    // Update table data whenever submissions change
    useEffect(() => {
        if (userState.status && submissions) {
            const tableData = submissions.map((submission, i) => {
                return {
                    id: submission._id,
                    Name: `${submission.FirstName} ${submission.LastName}`,
                    Email: submission.Email || "",
                    Phone: submission.Phone || "",
                    DOB: submission.DOB ? new Date(submission.DOB).toLocaleDateString() : "",
                    SubmittedDate: submission.SignatureDate ? new Date(submission.SignatureDate).toLocaleDateString() : "",
                    EmergencyContact: `${submission.EmergencyContactFirstName || ''} ${submission.EmergencyContactLastName || ''}`.trim() || "N/A",

                    actions: (
                        <div className="actions-right">
                            <OverlayTrigger placement="top"
                                            overlay={<Tooltip id={"view_" + i}>View Submission</Tooltip>}>
                                <span>
                                <Button
                                    onClick={async () => {
                                        let tURL = "/admin/viewsubmission/" + submission._id;
                                        await history.push(tURL);
                                    }}
                                    color="info"
                                    size="sm"
                                    className={classNames("btn-icon btn-link like",)}
                                >
                                    <i className="tim-icons icon-zoom-split"/>
                                </Button>{" "}
                                </span>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top"
                                            overlay={<Tooltip id={"delete_" + i}>Delete Submission</Tooltip>}>
                                <span>
                                <Button
                                    onClick={() => {
                                        deleteSubmissionAlert(submission._id, `${submission.FirstName} ${submission.LastName}`);
                                    }}
                                    color="danger"
                                    size="sm"
                                    className={classNames("btn-icon btn-link like",)}
                                >
                                    <i className="tim-icons icon-simple-remove"/>
                                </Button>{" "}
                                </span>
                            </OverlayTrigger>
                        </div>
                    )
                };
            });
            setData(tableData);
        }
    }, [submissions, userState.status]);

    useEffect(() => {
        const getWaiverInfo = async () => {
            try {
                const response = await authenticatedFetch(`${rest.waivers}/${waiverId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                });
                const waiverData = await response.json();
                setWaiverInfo(waiverData);
            } catch (error) {
                console.error('Error fetching waiver info:', error);
                notify("danger", "Failed to load waiver information");
            }
        };

        const getSubmissions = async () => {
            try {
                const response = await authenticatedFetch(`${rest.waiversubmissions}/${waiverId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                });
                const submissionsData = await response.json();
                setSubmissions(submissionsData);
            } catch (error) {
                console.error('Error fetching submissions:', error);
                notify("danger", "Failed to load submissions");
            }
        };

        if (waiverId) {
            getWaiverInfo();
            getSubmissions();
        }
    }, [waiverId, dataLoaded]);

    if (userState.status) {
        return (
            <>
                {alert}
                <div className="rna-container">
                    <NotificationAlert ref={notificationAlertRef} />
                </div>
                <div className="content">
                    <Row className="mb-4">
                        <Col md={8} className="ml-auto mr-auto">
                            <h2 className="text-center">Waiver Submissions</h2>
                            {waiverInfo && (
                                <p className="text-center">
                                    {waiverInfo.Title} {waiverInfo.Version && `(v${waiverInfo.Version})`}
                                </p>
                            )}
                        </Col>
                    </Row>
                    <Row className="mt-5">
                        <Col xs={12} md={12}>
                            <Card>
                                <CardHeader>
                                    <Row>
                                        <Col md="6">
                                            <CardTitle tag="h4">Submissions ({submissions.length})</CardTitle>
                                        </Col>
                                        <Col md="6" className="text-right">
                                            <Button
                                                color="secondary"
                                                onClick={() => history.push("/admin/waivers")}
                                                className="animation-on-hover"
                                            >
                                                <i className="tim-icons icon-minimal-left"/> Back to Waivers
                                            </Button>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <ReactTable
                                        data={data}
                                        filterable
                                        columns={[
                                            {
                                                Header: "Name",
                                                accessor: "Name"
                                            },
                                            {
                                                Header: "Email",
                                                accessor: "Email"
                                            },
                                            {
                                                Header: "Phone",
                                                accessor: "Phone"
                                            },
                                            {
                                                Header: "Date of Birth",
                                                accessor: "DOB"
                                            },
                                            {
                                                Header: "Emergency Contact",
                                                accessor: "EmergencyContact"
                                            },
                                            {
                                                Header: "Submitted Date",
                                                accessor: "SubmittedDate"
                                            },
                                            {
                                                Header: "Actions",
                                                accessor: "actions",
                                                sortable: false,
                                                filterable: false
                                            }
                                        ]}
                                        defaultPageSize={10}
                                        showPaginationTop
                                        showPaginationBottom={false}
                                        className="-striped -highlight"
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </>

        );
    }

    else {
        history.push("/admin/dashboard");
        return(null);
    }
};

export default WaiverSubmissionsTable;
